import { Box, type BoxProps } from "../../components/ui/box";
import { ScrollArea } from "../../components/ui/scroll-area";
import { Text, type TextProps } from "../../components/ui/text";
import type React from "react";
import type { Omittable } from "../../lib/props";

/**
 * The sidebar pane: the rounded `sidebar`-surface column that fills the
 * sidebar region — a fixed header, a scrolling body of sections, and an
 * optional pinned footer.
 *
 * ## The body and footer carry the same 12px gutter, and nothing else does
 *
 * ```
 *   │← 12 →│← 8 →│ text
 *   │gutter│ row │
 *   └region┴ row's own padding = the 20px reading edge
 * ```
 *
 * The gutter lives on the body's content column and the footer — NOT on the
 * pane — so the body's ScrollArea spans the full pane width and its scrollbar
 * hugs the pane edge instead of floating 12px inside it. The header is the
 * one exception: it frames the pane with a 16px inset on its open sides
 * instead of sitting in the gutter.
 *
 * Everything inside is full-bleed within that gutter. An interactive box — a
 * menu button, a section heading's collapse trigger, an icon button, the
 * footer button — spans the content column, and its OWN padding is what
 * carries text and glyphs to the reading edge. That is deliberate: a hover
 * wash bleeds the gutter's width past the text it highlights, which is what
 * makes a hovered row read as a row rather than as a box drawn around a label.
 *
 * Two numbers, one owner each. `SidebarGroup` and `SidebarSeparator` are
 * vertical-only for exactly this reason — a group that padded horizontally
 * would push its rows to 24 while the header stayed at 8.
 *
 * The title does not chase the rows' reading edge: it sits flush with the
 * header's 16px frame, 4px shy of the row text below.
 */
export function SidebarPane({
  children,
  ...props
}: BoxProps): React.ReactElement {
  return (
    <Box
      surface="sidebar"
      direction="column"
      fill="flex"
      // Pane-scoped hover group: chrome anywhere in the pane can reveal on
      // pointer entry. The header keeps its own scope for header-only chrome.
      group="pane"
      // Clips section rows to the rounded corners.
      overflow="hidden"
      rounded="lg"
      // The right edge butts flush against the content card, so only the outer
      // corners round and the shared seam reads as one straight line. A pane in
      // a mobile drawer is free-standing — pass `roundedSide={undefined}`.
      roundedSide="left"
      // The surface prop supplies only the border COLOR; the hairline itself
      // must be asked for. Open edges only — the right edge is the content
      // card's seam and stays borderless so the columns read as siblings.
      border={["top", "left", "bottom"]}
      {...props}
    >
      {children}
    </Box>
  );
}

export type SidebarPaneHeaderProps = Omittable<{
  /** Leading content — a title, or a back control followed by one. */
  children: React.ReactNode;
  /** Trailing controls, pinned to the pane's far edge. */
  actions?: React.ReactNode;
}>;

/**
 * The pane's fixed header. Sized identically across panes so the title holds
 * its line when a route swaps one pane for another.
 */
export function SidebarPaneHeader({
  children,
  actions,
}: SidebarPaneHeaderProps): React.ReactElement {
  return (
    <Box
      align="center"
      direction="row"
      gap="xs"
      group="header"
      justify="between"
      // The header sits inset from the body's 12px gutter (see the pane
      // comment): a 16px frame on its open sides, the body's 8px rhythm below.
      paddingX="md"
      paddingTop="md"
      paddingBottom="sm"
      shrink={false}
    >
      {/* Leading content packs left and absorbs the slack, so the title's
          position does not depend on whether the pane has actions. */}
      <Box align="center" direction="row" gap="xs" grow minWidth={0}>
        {children}
      </Box>
      {actions != null && (
        <Box align="center" direction="row" gap="xs" shrink={false}>
          {actions}
        </Box>
      )}
    </Box>
  );
}

/**
 * The pane's title, flush with the header's own inset. Use it for a title
 * that LEADS the header; a title that follows a control (a back button) is
 * already past the edge and takes a plain `Text`.
 */
export function SidebarPaneTitle({
  children,
  ...props
}: TextProps): React.ReactElement {
  return (
    <Box minWidth={0}>
      <Text size="xl" truncate weight="bold" {...props}>
        {children}
      </Text>
    </Box>
  );
}

/**
 * The scrolling section list. Takes whatever the header and pinned footer
 * leave — `minHeight={0}` is what lets it shrink below its content.
 */
export function SidebarPaneBody({
  children,
}: Omittable<{ children: React.ReactNode }>): React.ReactElement {
  return (
    <Box direction="column" grow minWidth={0} minHeight={0}>
      {/* The ScrollArea is full-bleed so the scrollbar rides the pane edge;
          the gutter sits on the content column inside it. */}
      <ScrollArea scrollFade>
        <Box paddingX="base">{children}</Box>
      </ScrollArea>
    </Box>
  );
}

/**
 * Pinned footer — outside the scroll region, so scrolling never moves it. Its
 * slot always occupies space, so revealing hover chrome inside it never shifts
 * the sections above.
 */
export function SidebarPaneFooter({
  children,
}: Omittable<{ children: React.ReactNode }>): React.ReactElement {
  return (
    <Box paddingX="base" paddingY="sm" shrink={false}>
      {children}
    </Box>
  );
}
